import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { money, orderLabels, shortDate, stageLabels } from '../lib/format';
import {
  EMPTY_ORDER_FILTERS,
  ORDER_SORT_LABELS,
  ORDER_VIEW_LABELS,
  PAYMENT_FILTER_LABELS,
  activeFilterCount,
  loadOrderFilters,
  orderFiltersQuery,
  saveOrderFilters,
  type OrderFilters,
} from '../lib/order-filters';
import type {
  ListResponse,
  Order,
  OrderSort,
  OrderStatus,
  OrderView,
  PaymentFilter,
} from '../types';
import { NewOrderModal } from '../components/new-order-modal';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
} from '../components/ui';

const PAGE_SIZE = 20;
const VIEWS = Object.keys(ORDER_VIEW_LABELS) as OrderView[];

type BadgeTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

function orderCardPresentation(order: Order): { label: string; tone: BadgeTone } {
  if (order.deadlineState === 'OVERDUE') {
    return { label: stageLabels[order.currentStage], tone: 'danger' };
  }

  if (order.status === 'WAITING_PAYMENT') {
    return { label: orderLabels[order.status], tone: 'warning' };
  }

  if (['SKETCH_REVIEW', 'WAITING_REVISIONS', 'FINAL_REVIEW'].includes(order.currentStage)) {
    return { label: stageLabels[order.currentStage], tone: 'warning' };
  }

  if (['READY', 'DELIVERED'].includes(order.currentStage)) {
    return {
      label: stageLabels[order.currentStage],
      tone: Number(order.payment.balanceDue) > 0 ? 'warning' : 'success',
    };
  }

  if (order.status === 'COMPLETED' || order.currentStage === 'COMPLETED') {
    return { label: stageLabels[order.currentStage], tone: 'success' };
  }

  if (order.status === 'PAUSED' || order.status === 'CANCELED') {
    return { label: orderLabels[order.status], tone: 'neutral' };
  }

  if (['SKETCH', 'LINEART', 'COLORING', 'DETAILING', 'REVISION'].includes(order.currentStage)) {
    return { label: stageLabels[order.currentStage], tone: 'info' };
  }

  return { label: stageLabels[order.currentStage], tone: 'neutral' };
}

export function OrdersPage() {
  const [filters, setFilters] = useState<OrderFilters>(loadOrderFilters);
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const query = useQuery({
    queryKey: ['orders', filters, page],
    queryFn: () =>
      api<ListResponse<Order>>(
        `/orders?${orderFiltersQuery(filters, PAGE_SIZE, page * PAGE_SIZE)}`,
      ),
  });
  const total = query.data?.total ?? 0;
  const extraFilters = activeFilterCount(filters);

  useEffect(() => saveOrderFilters(filters), [filters]);

  const change = (patch: Partial<OrderFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(0);
  };

  return (
    <div className="orders-page">
      <PageHeader
        eyebrow="Рабочий поток"
        title="Заказы"
        action={
          <Button variant="ghost" onClick={() => setCreating(true)}>
            + Заказ
          </Button>
        }
      />

      <Field label="Поиск" hint="Номер, название или имя заказчика">
        <Input
          value={filters.query}
          placeholder="AF-2026-0007 или Анна"
          onChange={(event) => change({ query: event.target.value })}
        />
      </Field>

      {/* Быстрые представления отвечают на «что делать сейчас» одним нажатием. */}
      <div className="tabs tabs--scroll">
        <button
          className={filters.view === '' ? 'is-active' : ''}
          onClick={() => change({ view: '' })}
        >
          Все
        </button>
        {VIEWS.map((view) => (
          <button
            key={view}
            className={filters.view === view ? 'is-active' : ''}
            onClick={() => change({ view: filters.view === view ? '' : view })}
          >
            {ORDER_VIEW_LABELS[view]}
          </button>
        ))}
      </div>

      <div className="list-toolbar">
        <Button variant="ghost" onClick={() => setFiltersOpen(true)}>
          Фильтры{extraFilters > 0 ? ` · ${extraFilters}` : ''}
        </Button>
        <Select
          aria-label="Сортировка"
          value={filters.sort}
          onChange={(event) => change({ sort: event.target.value as OrderSort })}
        >
          {(Object.keys(ORDER_SORT_LABELS) as OrderSort[]).map((sort) => (
            <option key={sort} value={sort}>
              {ORDER_SORT_LABELS[sort]}
            </option>
          ))}
        </Select>
      </div>

      {query.isPending && <LoadingScreen label="Собираем ваши заказы" />}
      {query.error && <ErrorNotice error={query.error} />}
      {query.data?.items.length === 0 && (
        <EmptyState
          title="Заказов нет"
          text={
            filters.query || filters.view || extraFilters > 0
              ? 'Под такие условия ничего не нашлось — попробуйте снять пару фильтров.'
              : 'Принятые заявки станут заказами и появятся здесь. А если договорились где-то ещё — заведите заказ кнопкой «+ Заказ».'
          }
        />
      )}

      <div className="list order-list">
        {query.data?.items.map((order) => {
          const totalPrice = Number(order.totalPrice);
          const paidAmount = Number(order.payment.paidAmount);
          const paymentPercent =
            totalPrice > 0
              ? Math.min(100, Math.max(0, Math.round((paidAmount / totalPrice) * 100)))
              : paidAmount > 0
                ? 100
                : 0;
          const presentation = orderCardPresentation(order);

          return (
            <Link key={order.id} href={`/orders/${order.id}`} className="card order-card">
              <div className="order-card__person">
                <span className="order-card__avatar" aria-hidden="true">
                  {order.client.displayName.slice(0, 1).toUpperCase()}
                </span>
                <span className="order-card__identity">
                  <strong>{order.client.displayName}</strong>
                  <small>
                    {order.publicNumber} · {shortDate(order.deadline)}
                  </small>
                </span>
                <StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge>
              </div>
              <div className="order-card__brief">
                <h2>{order.title}</h2>
                <p>
                  {order.service?.title ?? 'Индивидуальный заказ'} · {order.progress}% готово
                </p>
              </div>
              {order.tags.length > 0 && (
                <div className="tag-list" aria-label="Теги заказа">
                  {order.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="tag-pill">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="payment-progress__label">
                <span>
                  Оплачено {money(order.payment.paidAmount, order.currency)} из{' '}
                  {money(order.totalPrice, order.currency)}
                </span>
                <strong>{paymentPercent}%</strong>
              </div>
              <div className="payment-progress">
                <span style={{ width: `${paymentPercent}%` }} />
              </div>
              <div className="card-row card-row--bottom">
                <span className={order.deadlineState === 'OVERDUE' ? 'text-danger' : ''}>
                  {order.deadlineState === 'OVERDUE' ? 'Просрочен · ' : 'Срок · '}
                  {shortDate(order.deadline)}
                </span>
                <strong className="numeric">{money(order.totalPrice, order.currency)}</strong>
              </div>
            </Link>
          );
        })}
      </div>

      {total > PAGE_SIZE && (
        <div className="pager">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Назад
          </Button>
          <span className="meta">
            {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} из {total}
          </span>
          <Button
            variant="ghost"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(page + 1)}
          >
            Вперёд
          </Button>
        </div>
      )}

      {filtersOpen && (
        <Modal title="Фильтры" onClose={() => setFiltersOpen(false)}>
          <div className="stack">
            <Field label="Статус">
              <Select
                value={filters.status}
                onChange={(event) => change({ status: event.target.value as OrderStatus | '' })}
              >
                <option value="">Любой</option>
                {(Object.keys(orderLabels) as OrderStatus[]).map((status) => (
                  <option key={status} value={status}>
                    {orderLabels[status]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Оплата">
              <Select
                value={filters.payment}
                onChange={(event) => change({ payment: event.target.value as PaymentFilter | '' })}
              >
                <option value="">Любая</option>
                {(Object.keys(PAYMENT_FILTER_LABELS) as PaymentFilter[]).map((state) => (
                  <option key={state} value={state}>
                    {PAYMENT_FILTER_LABELS[state]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Тег" hint="Один тег заказа">
              <Input
                value={filters.tag}
                onChange={(event) => change({ tag: event.target.value })}
              />
            </Field>
            <div className="form-grid">
              <Field label="Дедлайн с">
                <Input
                  type="date"
                  value={filters.deadlineFrom}
                  onChange={(event) => change({ deadlineFrom: event.target.value })}
                />
              </Field>
              <Field label="Дедлайн по">
                <Input
                  type="date"
                  value={filters.deadlineTo}
                  onChange={(event) => change({ deadlineTo: event.target.value })}
                />
              </Field>
            </div>
            <label className="toggle-row">
              <span>Только архивные</span>
              <button
                type="button"
                role="switch"
                aria-checked={filters.archived}
                aria-label="Только архивные"
                className={`switch ${filters.archived ? 'is-on' : ''}`}
                onClick={() => change({ archived: !filters.archived })}
              >
                <span />
              </button>
            </label>
            <Button wide onClick={() => setFiltersOpen(false)}>
              Показать
            </Button>
            <Button
              variant="ghost"
              wide
              onClick={() => {
                setFilters(EMPTY_ORDER_FILTERS);
                setPage(0);
              }}
            >
              Сбросить всё
            </Button>
          </div>
        </Modal>
      )}

      {creating && <NewOrderModal onClose={() => setCreating(false)} />}
    </div>
  );
}
